"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.styledVirtualizedList = exports.styledSectionList = exports.styledFlatList = exports.SharedValue = exports.FontSizeContext = exports.RemContext = exports.defaultUnits = void 0;
/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const convertStyle_1 = __importDefault(require("./convertStyle"));
const cssToRN_1 = __importDefault(require("./cssToRN"));
const features_1 = require("./features");
const generateHash_1 = __importDefault(require("./generateHash"));
exports.defaultUnits = { em: 16, vw: 1, vh: 1, vmin: 1, vmax: 1, rem: 16, px: 1, pt: 72 / 96, in: 96, pc: 9, cm: 96 / 2.54, mm: 96 / 25.4 };
exports.RemContext = react_1.default.createContext(exports.defaultUnits.rem);
exports.FontSizeContext = react_1.default.createContext(exports.defaultUnits.em);
// We use this to share value within the component (Theme, Translation, whatever)
exports.SharedValue = react_1.default.createContext(undefined);
function buildCSSString(chunks, functs, props, shared) {
    let computedString = chunks.map((chunk, i) => ([chunk, (functs[i] instanceof Function) ? functs[i]({ shared, theme: shared, ...props }) : functs[i]])).flat().join('');
    if (props.rnCSS)
        computedString += props.rnCSS.replace(/=/gm, ':') + ';';
    return computedString;
}
const styleMap = {};
function getStyle(hash, style) {
    const styleInfo = styleMap[hash];
    if (styleInfo) {
        styleInfo.usage++;
        return styleInfo.style;
    }
    else {
        const sheet = react_native_1.StyleSheet.create({ [hash]: style });
        return (styleMap[hash] = { style: sheet[hash], usage: 1 }).style;
    }
}
function removeStyle(hash) {
    styleMap[hash].usage--;
    if (styleMap[hash].usage <= 0)
        delete styleMap[hash];
}
const styled = (Component) => {
    const styledComponent = (chunks, ...functs) => {
        const ForwardRefComponent = react_1.default.forwardRef((props, ref) => {
            const rem = react_1.default.useContext(exports.RemContext);
            const shared = react_1.default.useContext(exports.SharedValue);
            // Build the css string with the context
            const css = react_1.default.useMemo(() => buildCSSString(chunks, functs, props, shared), [props, shared]);
            // Store the style in RN format
            const rnStyle = react_1.default.useMemo(() => (0, cssToRN_1.default)(css), [css]);
            const { needsLayout, needsHover } = react_1.default.useMemo(() => ({
                // needsFontSize: !!css.match(/\b(\d+)(\.\d+)?em\b/)
                // needsScreenSize: !!css.match(/\b(\d+)(\.\d*)?v([hw]|min|max)\b/) || !!rnStyle.media,
                needsLayout: !!css.match(/\d%/),
                needsHover: !!rnStyle.hover
            }), [css, rnStyle.hover]);
            // Handle hover
            const { onMouseEnter, onMouseLeave, hover } = (0, features_1.useHover)(props.onMouseEnter, props.onMouseLeave, needsHover);
            const tempStyle = react_1.default.useMemo(() => {
                const style = { ...rnStyle };
                delete style.media;
                delete style.hover;
                if (hover)
                    Object.assign(style, rnStyle.hover);
                return style;
            }, [hover, rnStyle]);
            // Calculate current em unit for media-queries
            const parentEm = react_1.default.useContext(exports.FontSizeContext);
            const { em: tempEm } = (0, features_1.useFontSize)(tempStyle.fontSize, rem, parentEm);
            // Handle layout data needed for % units
            const { width, height, onLayout } = (0, features_1.useLayout)(props.onLayout, needsLayout);
            // Handle screen size needed for vw and wh units
            const screenUnits = (0, features_1.useScreenSize)();
            /** The units before re-updating the em */
            const baseUnits = react_1.default.useMemo(() => ({
                ...exports.defaultUnits,
                rem,
                em: tempEm,
                width,
                height,
                ...screenUnits
            }), [height, rem, screenUnits, tempEm, width]);
            // apply media queries
            const mediaQuery = (0, features_1.useMediaQuery)(rnStyle.media, baseUnits);
            // Handle em units
            const fontSize = (mediaQuery && mediaQuery.fontSize) || tempStyle.fontSize;
            const { em } = (0, features_1.useFontSize)(fontSize, baseUnits.rem, parentEm);
            const finalStyle = react_1.default.useMemo(() => {
                const style = { ...tempStyle, ...mediaQuery };
                if (style.fontSize)
                    style.fontSize = em + 'px';
                if (style.zIndex === undefined && react_native_1.Platform.OS === 'web')
                    style.zIndex = 'auto';
                return style;
            }, [em, mediaQuery, tempStyle]);
            const units = react_1.default.useMemo(() => ({ ...baseUnits, em }), [baseUnits, em]);
            const { style: styleConvertedFromCSS, hash } = react_1.default.useMemo(() => {
                const style = (0, convertStyle_1.default)(finalStyle, units);
                delete style.textOverflow;
                const hash = (0, generateHash_1.default)(JSON.stringify(style));
                return { style: getStyle(hash, style), hash };
            }, [finalStyle, units]);
            const newProps = react_1.default.useMemo(() => {
                const newProps = { style: [styleConvertedFromCSS, props.style], onMouseEnter, onMouseLeave, onLayout };
                if (finalStyle.textOverflow === 'ellipsis') {
                    Object.assign(newProps, { numberOfLines: 1 });
                }
                return newProps;
            }, [finalStyle.textOverflow, onLayout, onMouseEnter, onMouseLeave, props.style, styleConvertedFromCSS]);
            react_1.default.useEffect(() => () => removeStyle(hash), [hash]);
            // em !== parentEm alone is a bit dangerous as the component would rerender when the font size change
            if (em !== parentEm || finalStyle.fontSize !== undefined) {
                return react_1.default.createElement(exports.FontSizeContext.Provider, { value: em },
                    react_1.default.createElement(Component, { ref: ref, ...props, ...newProps }));
            }
            else {
                return react_1.default.createElement(Component, { ref: ref, ...props, ...newProps });
            }
        });
        return ForwardRefComponent;
    };
    // provide styled(Comp).attrs({} | () => {}) feature
    styledComponent.attrs = (opts) => (chunks, ...functs) => {
        const ComponentWithAttrs = styledComponent(chunks, ...functs);
        const ForwardRefComponent = react_1.default.forwardRef((props, ref) => {
            const attrs = (opts instanceof Function) ? opts(props) : opts;
            return react_1.default.createElement(ComponentWithAttrs, { ref: ref, ...props, ...attrs });
        });
        // TODO : Find a way to remove from the Props the properties affected by opts
        return ForwardRefComponent;
    };
    return styledComponent;
};
exports.default = styled;
const styledFlatList = (chunks, ...functs) => (props) => invoke(styled(react_native_1.FlatList)(chunks, ...functs), props);
exports.styledFlatList = styledFlatList;
exports.styledFlatList.attrs = (opts) => (chunks, ...functs) => (componentProps) => invoke(styled(react_native_1.FlatList).attrs(opts)(chunks, ...functs), componentProps);
const styledSectionList = (chunks, ...functs) => (props) => invoke(styled(react_native_1.SectionList)(chunks, ...functs), props);
exports.styledSectionList = styledSectionList;
exports.styledSectionList.attrs = (opts) => (chunks, ...functs) => (componentProps) => invoke(styled(react_native_1.SectionList).attrs(opts)(chunks, ...functs), componentProps);
const styledVirtualizedList = (chunks, ...functs) => (props) => invoke(styled(react_native_1.VirtualizedList)(chunks, ...functs), props);
exports.styledVirtualizedList = styledVirtualizedList;
exports.styledVirtualizedList.attrs = (opts) => (chunks, ...functs) => (componentProps) => invoke(styled(react_native_1.VirtualizedList).attrs(opts)(chunks, ...functs), componentProps);
function invoke(Component, props) {
    return react_1.default.createElement(Component, { ...props });
}
